import { SupabaseClient } from "@supabase/supabase-js";
import { logException } from "@punchup/sentry";
import { sanitizeIlikeSearch } from "@punchup/utils/sanitizeIlikeSearch";
import { Database } from "../../supabase.types";
import {
  GateUserCandidate,
  GateVenueCandidate,
} from "../../models/statsig-gate-members";

/** Cap on candidates returned to the gate-member search box. */
const SEARCH_LIMIT = 10;

type Supabase = SupabaseClient<Database>;

/**
 * `users_extra.id` and `venue_pages.id` are `uuid` columns, so `.eq("id", term)`
 * with a non-UUID term (an email, a venue name) is rejected by Postgres with
 * `invalid input syntax for type uuid` — which would abort the whole search.
 * Only run the id branch when the term is actually shaped like a UUID.
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

/**
 * Resolves a search term to `userID` candidates for a user-keyed gate.
 *
 * Matches email OR the raw id (so ops can paste a known user id and confirm it),
 * mirroring the existing internal user-search route. The returned `id` is the
 * value written into the gate override.
 */
export async function searchGateUsers(
  supabase: Supabase,
  query: string,
  limit: number = SEARCH_LIMIT
): Promise<GateUserCandidate[]> {
  const safe = sanitizeIlikeSearch(query);
  if (safe.length === 0) return [];

  const [emailResp, idResp] = await Promise.all([
    supabase
      .from("users_extra")
      .select("id, email, phone, last_active_at")
      .ilike("email", `%${safe}%`)
      .order("last_active_at", { ascending: false, nullsFirst: false })
      .limit(limit),
    // Skip the id lookup for a non-UUID term (an email) — see isUuid.
    isUuid(safe)
      ? supabase
          .from("users_extra")
          .select("id, email, phone, last_active_at")
          .eq("id", safe)
          .limit(limit)
      : null,
  ]);

  if (emailResp.error) {
    logException(emailResp.error, {
      data: "[statsig-gate-resolvers] Failed to search users by email",
    });
    throw emailResp.error;
  }
  if (idResp?.error) {
    logException(idResp.error, {
      data: "[statsig-gate-resolvers] Failed to search users by id",
    });
    throw idResp.error;
  }

  const seen = new Set<string>();
  const candidates: GateUserCandidate[] = [];
  for (const row of [...(idResp?.data ?? []), ...(emailResp.data ?? [])]) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    candidates.push({
      unitType: "userID",
      id: row.id,
      email: row.email ?? "",
      phone: row.phone ?? null,
      lastActiveAt: row.last_active_at ?? null,
    });
  }

  return candidates.slice(0, limit);
}

/**
 * Resolves a search term to `venuePageId` candidates for a venue-keyed gate.
 *
 * Matches venue name OR the raw id. The returned `id` is the `venue_pages` row
 * id (NOT the slug) — the value the gate is bucketed on.
 */
export async function searchGateVenues(
  supabase: Supabase,
  query: string,
  limit: number = SEARCH_LIMIT
): Promise<GateVenueCandidate[]> {
  const safe = sanitizeIlikeSearch(query);
  if (safe.length === 0) return [];

  const [nameResp, idResp] = await Promise.all([
    supabase
      .from("venue_pages")
      .select("id, name, slug, is_live")
      .ilike("name", `%${safe}%`)
      .order("name", { ascending: true })
      .limit(limit),
    // Skip the id lookup for a non-UUID term (a venue name) — see isUuid.
    isUuid(safe)
      ? supabase
          .from("venue_pages")
          .select("id, name, slug, is_live")
          .eq("id", safe)
          .limit(limit)
      : null,
  ]);

  if (nameResp.error) {
    logException(nameResp.error, {
      data: "[statsig-gate-resolvers] Failed to search venues by name",
    });
    throw nameResp.error;
  }
  if (idResp?.error) {
    logException(idResp.error, {
      data: "[statsig-gate-resolvers] Failed to search venues by id",
    });
    throw idResp.error;
  }

  const seen = new Set<string>();
  const candidates: GateVenueCandidate[] = [];
  for (const row of [...(idResp?.data ?? []), ...(nameResp.data ?? [])]) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    candidates.push({
      unitType: "venuePageId",
      id: row.id,
      name: row.name,
      slug: row.slug,
      isLive: row.is_live,
    });
  }

  return candidates.slice(0, limit);
}
